import {Collapse, Tree, Checkbox, CheckboxProps} from "antd";
import {DataNode} from "antd/es/tree";
import {Key, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useAppDispatch, useAppSelector} from "../../app/store";
import {setFilterField} from "../../app/store/reducers/appSlice";
import {deunify, unify} from "../../app/utils/strings";
import {FilterContainer, TreeContainer, FilterBlock} from "./styles";
import {localeFilter} from "./const";
import {localeColumns} from "../grid/const";
import i18n from "../../i18n";

enum Fields {
    Activity = 'activity',
    Country = 'country',
    ShipTech = 'st',
}

const ACTIVITY_KEY_SUFFIX = "-parent"

export default function Filter() {
    const dispatch = useAppDispatch()
    const {t} = useTranslation()
    const {activities, countries, filter} = useAppSelector(store => store.AppSlice)
    const lang = i18n.language as "ru" | "en"

    const collapseKeys = window.innerWidth > 768 ? ["countries", "activities"] : []

    const countryTreeData = useMemo((): DataNode[] => {
        const orderKey = lang === 'en' ? 'orderEn' : 'orderRu'
        return countries.slice().sort((a, b) => a[orderKey] - b[orderKey]).map(country => ({
            key: country.value,
            title: country[localeFilter.title[lang]]
        }))
    }, [countries, lang])

    const activitiesTreeData = useMemo((): DataNode[] => activities.map(activity => ({
        key: unify(activity.value, ACTIVITY_KEY_SUFFIX),
        title: activity[localeFilter.title[lang]],
        children: activity.options.map(item => ({key: item.value, title: item[localeFilter.title[lang]]}))
    })), [activities, lang])

    const onCountrySelect = (keys: Key[]) => {
        const value = keys.length ? keys[0] : ""
        dispatch(setFilterField({name: Fields.Country, value}))
    }

    const onActivityCheck = (checkedKeys: { checked: Key[]; halfChecked: Key[] }, info: any) => {
        const { node, checked } = info;
        const isParentNode = !!node.children;

        let newActivityValues: Key[] = [];

        if (isParentNode && checked) {
            // Выбрана группа - получаем все дочерние элементы
            const activityGroup = activities.find(item =>
                item.value.toString() === deunify(node.key.toString(), ACTIVITY_KEY_SUFFIX)
            );

            if (activityGroup) {
                newActivityValues = [...(filter.activity || []), ...activityGroup.options.map(item => item.value)];
            }
        } else if (isParentNode && !checked) {
            // Снята группа - удаляем все дочерние элементы
            const activityGroup = activities.find(item =>
                item.value.toString() === deunify(node.key.toString(), ACTIVITY_KEY_SUFFIX)
            );

            if (activityGroup) {
                newActivityValues = (filter.activity || []).filter(activityId =>
                    !activityGroup.options.some(option => option.value.toString() === activityId.toString())
                );
            }
        } else if (!isParentNode && checked) {
            // Выбран конкретный вид деятельности
            newActivityValues = [...(filter.activity || []), node.key];
        } else if (!isParentNode && !checked) {
            // Снят конкретный вид деятельности
            newActivityValues = (filter.activity || []).filter(activityId => activityId !== node.key);
        }

        // Убираем дубликаты
        newActivityValues = Array.from(new Set(newActivityValues));

        dispatch(setFilterField({name: Fields.Activity, value: newActivityValues}))
    }

    const onChangeCheckBox: CheckboxProps['onChange'] = (e) => {
        dispatch(setFilterField({name: Fields.ShipTech, value: e.target.checked}))
    };

    // Преобразуем выбранные activity в ключи для Tree
    const getActivityTreeKeys = useMemo(() => {
        const selectedActivities = filter.activity || [];
        const checkedKeys: Key[] = [];

        selectedActivities.forEach(activityId => {
            // Проверяем, является ли activityId дочерним элементом
            const isChild = activities.some(activity =>
                activity.options.some(option => option.value.toString() === activityId.toString())
            );

            if (isChild) {
                checkedKeys.push(activityId);
            } else {
                // Если это группа, добавляем родительский ключ
                const parentActivity = activities.find(activity =>
                    activity.value.toString() === activityId.toString()
                );
                if (parentActivity) {
                    checkedKeys.push(unify(parentActivity.value, ACTIVITY_KEY_SUFFIX));
                }
            }
        });

        return { checked: checkedKeys, halfChecked: [] };
    }, [filter.activity, activities]);

    return (
        <FilterContainer>
            <Collapse defaultActiveKey={collapseKeys}>
                <Collapse.Panel key="activities" header={t("Activities")}>
                    <TreeContainer>
                        <Tree
                            className="activities-tree"
                            treeData={activitiesTreeData}
                            onCheck={onActivityCheck}
                            checkedKeys={getActivityTreeKeys}
                            checkable={true}
                            selectable={false}
                        />
                    </TreeContainer>
                </Collapse.Panel>
                <Collapse.Panel key="countries" header={t("Country")}>
                    <TreeContainer>
                        <Tree
                            className="countries-tree"
                            treeData={countryTreeData}
                            onSelect={onCountrySelect}
                            selectedKeys={filter.country ? [filter.country] : []}
                            checkable={false}
                        />
                    </TreeContainer>
                </Collapse.Panel>
            </Collapse>
        </FilterContainer>
    )
}